import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Divider } from "@mui/material";
import {
  commentsApi,
  contentTypeAPIs,
  FETCH_TAG_LIST_QUERY,
  useComment,
  useSlugValidation,
  useWorkflow,
} from "@platformx/authoring-apis";
import { previewContent, RootState } from "@platformx/authoring-state";
import { CommentListPanel } from "@platformx/comment-review";
import {
  appendBrandNameIfPresent,
  capitalizeFirstLetter,
  CATEGORY_CONTENT,
  CommonPlateformXDialog,
  formatPageUrl,
  getContentUrlPath,
  getCurrentLang,
  getSelectedBrandName,
  getSubDomain,
  handleHtmlTags,
  Loader,
  MAPPING,
  PlatFormXDateTimeFormat,
  ShowToastError,
  ShowToastSuccess,
  trimString,
  useAccess,
  useUserSession,
  workflowKeys,
} from "@platformx/utilities";
import { WorkflowHistory } from "@platformx/workflow-management";
import { DamContentGallery } from "@platformx/x-image-render";
import { JSX, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Analytics from "../../components/Analytics/Analytics";
import ContentPageScroll from "../../components/ContentPageScroll";
import ContentStatus from "../../components/ContentStatus/ContentStatus";
import { CreateHeader } from "../../components/CreateHeader/CreateHeader";
import FCMNotification from "../../components/FCMNotification/FCMNotification";
import Seo from "../../components/SEO/SEO";
import SocialShare from "../../components/SocialShare/SocialShare";
import { ContentType, ContentTypeTagName } from "../../enums/ContentType";
import { icons, NOTIFICATION_PERMISSION } from "../../utils/Constants";
import {
  contentStatusTitleSubTitles,
  getCategoryTags,
  notificationAllkey,
  notificationTitleSubTitles,
} from "../../utils/Helper";
import { resolveSlugDocType } from "../../utils/slugDocType";
import ChooseTags from "../ChooseTags";
import AddQuestion from "./components/addQuestion/AddQuestion";
import ImageVideo from "./components/ImageVideo";
import Result from "./components/results/Result";
import { TitleDescription } from "./components/TitleDescription";
import { DRAFT, PUBLISHED } from "./Utils/constants";
import {
  analyticsTitleSubTitles,
  fieldValidation,
  seoTitleSubTitles,
  socialShareTitleSubTitles,
} from "./Utils/helper";

type CreatePollProps = {
  isShared?: boolean;
};

export const CreatePoll = ({ isShared = false }: CreatePollProps): JSX.Element => {
  const ctype = capitalizeFirstLetter(ContentType.Poll);
  const dispatch = useDispatch();
  const { getWorkflowDetails, workflowRequest } = useWorkflow();
  const { t } = useTranslation();
  const params = useParams();
  const updateTempObj = useRef<any>({});
  const { currentContent } = useSelector((state: RootState) => state.content);
  const [getSession] = useUserSession();
  const { userInfo, role } = getSession();
  const { canAccessAction } = useAccess();
  /* Notification settings are driven by the logged-in user's session permissions */
  const hasNotificationAccess = canAccessAction(
    NOTIFICATION_PERMISSION.category,
    NOTIFICATION_PERMISSION.subCategory,
    NOTIFICATION_PERMISSION.action,
  );
  const username = `${userInfo.first_name} ${userInfo.last_name}`;
  const pollPageUrl = new URL(window.location.href);
  const [isDraft, setIsDraft] = useState<boolean>(true);
  const [draftPageURL, setDraftPageURL] = useState<string>("");
  const currentPollData = useRef(
    pollPageUrl.searchParams.get("path") ? (pollPageUrl.searchParams.get("path") as string) : "",
  );
  const [scrollToView, setScrollToView] = useState<any>();
  const [pollInstance, setPollInstance] = useState<any>({});
  const [showExitWarning, setShowExitWarning] = useState(false);
  const unsavedChanges = useRef<boolean>(false);
  const qusUnsavedChanges = useRef<boolean>(false);
  const [, setIsEdited] = useState<boolean>(false);
  const navigate = useNavigate();
  const [previewButton, setPreviewButton] = useState(true);
  const [previewTooltip, setPreviewTooltip] = useState(true);
  const [publishButton] = useState(false);
  const saveButton = false;
  const [galleryState, setGalleryState] = useState<boolean>(false);
  const galleryType = useRef<string>("Images");
  const [openAddQuestion, setOpenAddQuestion] = useState(false);
  const [isClickedQueList] = useState(false);
  const [, setPublishUrl] = useState("");
  const [openPageExistModal, setOpenPageExistModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tagData, setTagData] = useState<any>({});
  const [isFeatured, setIsFeatured] = useState(false);
  const [tagArr, setTagArr] = useState<any>([]);
  const [tagCategory, setTagCategory] = useState<any>([]);

  const [workflow, setWorkflow] = useState({});
  const [key, setKey] = useState("");
  const [answerId, setAnswerId] = useState("");
  const [parentToolTip, setParentToolTip] = useState("");
  const [addImage, setAddImage] = useState<boolean>(false);
  const scrollDebounceRef = useRef<any>(null);
  const [runFetchTagList] = useLazyQuery(FETCH_TAG_LIST_QUERY);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [timerState, setTimerState] = useState(
    localStorage.getItem("contentTypeTimerState") === "true",
  );
  const [lastmodifiedDate, setLastmodifiedDate] = useState(new Date().toISOString());
  const [enableWorkflowHistory, setEnableWorkflowHistory] = useState<boolean>(false);
  const [pollState, setPollState] = useState<any>({
    title: "",
    // short_title: "",
    // short_description: "",
    description: "",
    imagevideoURL: "",
    colorCode: "",
    thumbnailURL: "",
    socialShareImgURL: "",
    titleSocialShare: "",
    descriptionSocialShare: "",
    tagsSocialShare: [],
    analytics_enable: true,
    impression: false,
    contentInsight: false,
    seo_enable: true,
    seoShared: true,
    scoreBy: t("count"),
    tags: [],
    is_schedule_publish: false,
    schedule_publish_datetime: "",
    is_schedule_unpublish: false,
    schedule_unpublish_datetime: "",
    poll_title: "",
    poll_description: "",
    queBackgroundColor: "",
    queBackgroundImg: "",
    original_image: {},
    published_images: [],
    question_original_image: {},
    question_published_images: [],
    structure_data: "",
    ...notificationAllkey,
    content_behind_rendering: "",
  });
  const [answers, setAnswers] = useState<any>([
    { id: "1", option: "", image: "" },
    { id: "2", option: "", image: "" },
  ]);
  const [, setFieldChanges] = useState(false);
  const [isReload, setIsReload] = useState(false);
  const [isError, setIsError] = useState<any>([]);
  const slugValidation = useSlugValidation(resolveSlugDocType("poll"));
  const originalSlugRef = useRef<string>("");

  useEffect(() => {
    if (slugValidation.isSubmitCheck) return;
    if (slugValidation.status === "unique") {
      ShowToastSuccess(t("slug_available", { docType: "Poll" }));
    } else if (slugValidation.status === "not_unique") {
      ShowToastError(t("slug_not_unique", { docType: "Poll" }));
    }
  }, [slugValidation.status, slugValidation.isSubmitCheck]);

  const handleSlugChange = (event) => {
    const { value } = event.target;
    setPollState((prevState) => ({ ...prevState, slug: value }));
    slugValidation.invalidate();
  };

  const handleSlugBlur = () => {
    slugValidation.validate(pollState?.slug, { excludeValue: originalSlugRef.current });
  };

  // Single source of truth for "is the slug allowed to be saved" — called once
  // from every top-level Save/Publish entry point, BEFORE branching into
  // createPoll vs updatePOLL. Checking only inside updatePOLL left createPoll
  // unchecked; once a document is created in this session, isDraft flips to
  // false and every later save routes into updatePOLL even without a page
  // reload, so an empty/unverified slug could still reach it. Enforcing here,
  // ahead of that branch, guarantees the slug is always valid by the time
  // either function runs — no route through this component can skip it.
  const guardSlugThenProceed = async (): Promise<boolean> => {
    if (!pollState?.slug) {
      ShowToastError(t("slug_required"));
      return false;
    }
    const isUnique = await slugValidation.checkNow(pollState.slug, {
      excludeValue: originalSlugRef.current,
    });
    if (!isUnique) {
      ShowToastError(t("slug_url_not_unique", { docType: "Poll" }));
      return false;
    }
    // eslint-disable-next-line require-atomic-updates
    originalSlugRef.current = pollState.slug;
    return true;
  };

  const currentPollSlug = pollState?.slug || "";
  const selectedBrandRoute = formatPageUrl(getSelectedBrandName() || getCurrentLang());
  const slugField = {
    value: currentPollSlug,
    onChange: handleSlugChange,
    onBlur: handleSlugBlur,
    canonicalUrl: currentPollSlug
      ? `${getSubDomain()}/${selectedBrandRoute}${getContentUrlPath({ tagName: "poll", slug: currentPollSlug })}`
      : "",
  };

  const { comments } = useComment();
  const login_user_id = userInfo?.user_id;

  useEffect(() => {
    setIsReload(!isReload);
  }, [comments]);

  useEffect(() => {
    if (Object.keys(pollInstance).length === 0 && !params.id) {
      const newPoll = {
        CommonFields: {
          page: "",
          title: "",
          // short_title: "",
          description: "",
          //  short_description: "",
          category: "Poll",
          site_name: "PlatX",
          page_state: "",
          is_edit: false,
          seo_enable: true,
          analytics_enable: true,
          robot_txt: false,
          sitemap: false,
          analytics: "",
          others: "",
          structure_data: "",
          creationDate: new Date().toISOString(),
          modificationDate: new Date().toISOString(),
          tags: [],
          createdBy: username,
          parent_page_url: "/",
          current_page_url: "",
          page_lastmodifiedby: username,
          settings: {},
          ...notificationAllkey,
          content_behind_rendering: "",
        },
        ObjectFields: {
          background_content: {
            objectType: "image",
            Url: "",
            Title: "",
            Thumbnail: "",
            Color: "",
          },
          question_background_content: {
            objectType: "image",
            Url: "",
            Title: "",
            Thumbnail: "",
            Color: "",
            original_image: {},
            published_images: [],
          },
          display_scores: "Count",
          // background_color: "",
          poll_description: "",
          poll_title: "",
          poll_question: "",
          // poll_answer_image: "",
          options_compound_fields: [],
          original_image: {},
          published_images: [],
        },
      };
      setPollInstance(newPoll);
    }
    const fetchWorkflowDetails = async () => {
      setIsLoading(true);
      if (!currentPollData.current && !Object.keys(currentContent).length) {
        await getWorkflowDetails(role, login_user_id, setWorkflow, capitalizeFirstLetter(ctype));
      }
      setIsLoading(false);
    };
    fetchWorkflowDetails();
  }, []);
  const updateField = (updatedPartialObj) => {
    updateTempObj.current = updatedPartialObj;
    const newTempData = JSON.parse(JSON.stringify(pollInstance));
    const tempObjField = {
      background_content: {
        objectType: pollState?.imagevideoURL ? "image" : pollState?.colorCode ? "color" : "",
        Url: pollState?.imagevideoURL,
        Title: "",
        Thumbnail: pollState?.imagevideoURL,
        Color: pollState?.colorCode,
      },
      question_background_content: {
        objectType: pollState?.queBackgroundImg
          ? "image"
          : pollState?.queBackgroundColor
            ? "color"
            : "",
        Url: pollState?.queBackgroundImg,
        Title: "",
        Thumbnail: pollState?.queBackgroundImg,
        Color: pollState?.queBackgroundColor,
        original_image: pollState?.question_original_image,
        published_images: pollState?.question_published_images,
      },
      display_scores: pollState?.scoreBy,
      poll_description: pollState?.poll_description,
      poll_title: pollState?.poll_title,
      poll_question: pollState?.poll_title,
      // poll_answer_image: pollState?.queBackgroundImg,
      is_image_option: addImage,
      options_compound_fields: answers.map((ans) => {
        return {
          option_image: {
            url: addImage ? ans.image : "",
            ext: ans.ext || "",
            title: "Option Image",
          },
          is_correct: ans.status,
          option_text: ans.option,
          option_id: ans.id,
        };
      }),
      original_image: pollState?.original_image,
      published_images: pollState?.published_images,
    };
    const modifiedPoll = {
      ...newTempData,
      CommonFields: {
        ...newTempData.CommonFields,
        ...updatedPartialObj,
        // Add FCM notification fields
        notification: pollState?.notification,
        notification_title: pollState?.notification_title,
        notification_desc: pollState?.notification_desc,
        notification_image: pollState?.notification_image,
        notification_destination_path: pollState?.notification_destination_path,
        notification_channel: pollState?.notification_channel,
        notification_topics: pollState?.notification_topics,
        content_behind_rendering: pollState?.content_behind_rendering,
      },
      ObjectFields: {
        ...newTempData.ObjectFields,
        ...tempObjField,
      },
    };
    setPollInstance(modifiedPoll);
  };
  const defPoll = {
    imagevideoURL: "",
    title: "",
    description: "",
    // short_title: "",
    // short_description: "",
    tags: [],
  };
  const pollRef = useRef<any>(defPoll);
  const tagRef = useRef<any>([]);

  const updatePollSettings = (pageUrl) => {
    const PollSettings = {
      socialog_url: `${getSubDomain()}/${selectedBrandRoute}/Poll/${pageUrl}`,
      socialog_type: "poll",
      socialog_sitename: pollRef.current?.title
        ? trimString(handleHtmlTags(pollRef.current?.title), 100)
        : "poll",
      seo_title: pollRef.current?.short_title
        ? trimString(handleHtmlTags(pollRef.current?.short_title), 100)
        : "",
      socialog_title: pollRef.current?.titleSocialShare
        ? trimString(handleHtmlTags(pollRef.current?.titleSocialShare), 100)
        : "",
      socialog_twitter_title: pollRef.current?.titleSocialShare
        ? trimString(handleHtmlTags(pollRef.current?.titleSocialShare), 100)
        : "",
      socialog_description: pollRef.current?.descriptionSocialShare
        ? trimString(handleHtmlTags(pollRef.current?.descriptionSocialShare), 163)
        : "",
      socialog_twitter_description: pollRef.current?.descriptionSocialShare
        ? trimString(handleHtmlTags(pollRef.current?.descriptionSocialShare), 163)
        : "",
      socialog_twitter_url: `${getSubDomain()}/${selectedBrandRoute}/Poll/${pageUrl}`,
      keywords: pollState?.tagsSocialShare, //pollRef.current.tags,
      seo_keywords: pollRef.current.tags,
      seo_description: pollRef.current?.description
        ? trimString(handleHtmlTags(pollRef.current?.description), 163)
        : "",
      socialog_image: pollRef.current?.socialShareImgURL,
      socialog_twitter_image: pollRef.current?.socialShareImgURL,
      is_schedule_publish: pollState?.is_schedule_publish,
      schedule_publish_datetime: pollState?.schedule_publish_datetime,
      is_schedule_unpublish: pollState?.is_schedule_unpublish,
      schedule_unpublish_datetime: pollState?.schedule_unpublish_datetime,
    };
    return PollSettings;
  };

  const updateCurrentInstance = (pageURL) => {
    const updatedObj = {
      page: pageURL,
      title: pollRef?.current?.title,
      //short_title: pollRef?.current?.short_title ? pollRef?.current?.short_title : "",
      description: pollRef?.current?.description,
      // short_description: pollRef?.current?.short_description
      //   ? pollRef?.current?.short_description
      //   : "",
      tags: pollRef?.current?.tags ? pollRef?.current?.tags : tagRef?.current,
      current_page_url: `/${pageURL}`,
      settings: { ...updatePollSettings(pageURL) },
      // Add FCM notification fields
      notification: pollState?.notification,
      notification_title: pollState?.notification_title,
      notification_desc: pollState?.notification_desc,
      notification_image: pollState?.notification_image,
      notification_destination_path: pollState?.notification_destination_path,
      notification_channel: pollState?.notification_channel,
      notification_topics: pollState?.notification_topics,
      content_behind_rendering: pollState?.content_behind_rendering,
    };
    updateField(updatedObj);
  };
  const buildUrl = (page_state) => {
    const url =
      currentPollData.current !== ""
        ? page_state === "PUBLISHED"
          ? `${getSubDomain()}/${selectedBrandRoute}/Poll/${currentPollData.current}`
          : currentPollData.current
        : page_state === "PUBLISHED"
          ? `${getSubDomain()}/${selectedBrandRoute}/` +
            `poll/${pollState?.title?.replace(/[^A-Z0-9]+/gi, "-")?.toLowerCase()}`
          : pollState?.title?.replace(/[^A-Z0-9]+/gi, "-")?.toLowerCase();
    return url;
  };
  const updateStructureData = (pageState = "DRAFT") => {
    const PollStructureData = {
      "@context": "https://schema.org",
      "@type": "VoteAction",
      name: pollState?.poll_title,
      description: pollState?.poll_description,
      url: buildUrl(pageState),
      startTime: new Date().toISOString(),
      option: answers?.map((ans) => ans.option),
    };
    return PollStructureData;
  };
  const [createpollmutate] = useMutation(contentTypeAPIs.createContentType);
  const [updatepollmutate] = useMutation(contentTypeAPIs.updateContentType);
  const [publishpollmutate] = useMutation(contentTypeAPIs.publishContentType);
  const [runFetchContentByPath] = useLazyQuery(contentTypeAPIs.fetchContentByPath);
  const taglength = useRef();
  useEffect(() => {
    const {
      title,
      // short_title: shortTitle,
      description,
      scoreBy,
      imagevideoURL,
      colorCode,
    } = pollState;
    const emptyAnswers = answers.filter((ans) => ans.option === "");
    const emptyImageOptions = answers.filter((ans) => ans.image === "");
    if (
      title === "" ||
      // shortTitle === "" ||
      // shortDesc === "" ||
      description === "" ||
      (colorCode === "" && imagevideoURL === "") ||
      pollState?.poll_title === "" ||
      pollState?.poll_description === "" ||
      scoreBy === "" ||
      emptyAnswers.length > 0 ||
      (addImage && emptyImageOptions.length > 0) ||
      tagArr?.length === 0
    ) {
      setPreviewButton(true);
    } else {
      if (!currentPollData.current && isDraft) {
        setPreviewTooltip(false);
      } else {
        setPreviewButton(false);
      }
    }
  }, [pollState, answers, addImage, isDraft]);

  const publishPopup = useRef({
    publishTitle: "Congratulations!",
    publishDescription:
      "Your Poll has been sent for publishing & will be published in a few seconds.",
    publishCloseText: "Go to Listing",
    publishConfirmText: "View POLL",
  });
  const publishPoll = (pageURL) => {
    const pollToSend = {
      page: pageURL,
    };
    publishpollmutate({
      variables: {
        contentType: ctype,
        input: pollToSend,
      },
    })
      .then((resp) => {
        setIsLoading(false);
        if (
          pollState?.is_schedule_publish &&
          pollState?.schedule_publish_datetime &&
          !pollState?.is_schedule_unpublish
        ) {
          publishPopup.current = {
            ...publishPopup.current,
            publishDescription: `Your Poll has been scheduled to publish at ${PlatFormXDateTimeFormat(
              pollState?.schedule_publish_datetime,
            )}`,
          };
          setShowPublishConfirm(true);
        } else if (
          pollState?.is_schedule_unpublish &&
          pollState?.schedule_unpublish_datetime &&
          !pollState?.is_schedule_publish
        ) {
          publishPopup.current = {
            ...publishPopup.current,
            publishDescription: `Your Poll has been scheduled to unpublish at ${PlatFormXDateTimeFormat(
              pollState?.schedule_unpublish_datetime,
            )}`,
          };
          setShowPublishConfirm(true);
        } else if (
          pollState?.is_schedule_unpublish &&
          pollState?.schedule_unpublish_datetime &&
          pollState?.is_schedule_publish &&
          pollState?.schedule_publish_datetime
        ) {
          publishPopup.current = {
            ...publishPopup.current,
            publishDescription: `Your Poll has been scheduled to publish at ${PlatFormXDateTimeFormat(
              pollState?.schedule_publish_datetime,
            )} & scheduled to unpublish at ${PlatFormXDateTimeFormat(
              pollState?.schedule_unpublish_datetime,
            )}`,
          };
          setShowPublishConfirm(true);
        } else {
          setShowPublishConfirm(true);
        }
        setPublishUrl(resp?.data?.authoring_publishContent?.current_page_url);
      })
      .catch((error: any) => {
        const statusCode = error?.networkError?.statusCode || 0;

        if (error?.graphQLErrors?.length > 0) {
          ShowToastError(error.graphQLErrors[0].message, statusCode);
        } else {
          ShowToastError(t("api_error_toast"), statusCode);
        }
      });
  };
  const [pageStatus, setPageStatus] = useState(DRAFT);
  const [workflowStatus, setWorkflowStatus] = useState(true);
  const [showWorkflowSubmit, setShowWorkflowSubmit] = useState(false);
  const workflowSubmitRequest = async (workflowObj, status) => {
    const { success, workflow_status } = await workflowRequest(workflowObj, status);
    if (success) {
      workflow_status === workflowKeys.publish.toLowerCase() && status === workflowKeys.approve
        ? setShowPublishConfirm(true)
        : setShowWorkflowSubmit(true);
    }
  };

  // Builds background_content using the CDN GCB path when available, falling back to legacy fields.
  const buildBackgroundContent = () => {
    const gcbPath = pollState?.original_image?.original_image_relative_path || "";
    const gcbExt = pollState?.original_image?.ext || "";
    return {
      objectType: pollState?.imagevideoURL ? "image" : pollState?.colorCode ? "color" : "",
      Url: gcbPath,
      Title: "",
      Thumbnail: gcbPath,
      Color: pollState?.colorCode,
      ext: gcbExt,
    };
  };
  const createPoll = (
    pageState,
    IsDuplicate = false,
    isWorkflow = true,
    // props = {},
    // event_step = "",
  ) => {
    setIsLoading(true);
    const newTempData = JSON.parse(JSON.stringify(pollInstance));
    const tempObjField = {
      background_content: buildBackgroundContent(),
      question_background_content: {
        objectType: pollState?.queBackgroundImg
          ? "image"
          : pollState?.queBackgroundColor
            ? "color"
            : "",
        Url: pollState?.queBackgroundImg,
        Title: "",
        Thumbnail: pollState?.queBackgroundImg,
        Color: pollState?.queBackgroundColor,
        original_image: pollState?.question_original_image,
        published_images: pollState?.question_published_images,
      },
      display_scores: pollState?.scoreBy,
      poll_description: pollState?.poll_description,
      poll_title: pollState?.poll_title,
      poll_question: pollState?.poll_title,
      is_image_option: addImage,
      options_compound_fields: answers.map((ans) => {
        return {
          option_image: {
            url: addImage ? ans.image : "",
            ext: ans.ext || "",
            title: "Option Image",
          },
          is_correct: ans.status,
          option_text: ans.option,
          option_id: ans.id,
        };
      }),
      original_image: pollState?.original_image,
      published_images: pollState?.published_images,
    };
    const pollToSend = {
      ...newTempData,
      CommonFields: {
        ...newTempData.CommonFields,
        ...updateTempObj.current,
        page_state: pageState,
        structure_data: pollState?.structure_data,
        IsConfirm: IsDuplicate,
        seo_enable: pollState?.seo_enable,
        analytics_enable: pollState?.analytics_enable,
        is_featured: isFeatured,
        slug: pollState?.slug,
      },
      ObjectFields: {
        ...newTempData.ObjectFields,
        ...tempObjField,
      },
    };

    createpollmutate({
      variables: {
        contenttype: ctype,
        input: appendBrandNameIfPresent(pollToSend, isShared),
      },
      context: {
        skipBrandNameHeader: isShared,
      },
    })
      .then((resp) => {
        unsavedChanges.current = false;
        setTimerState(true);
        setLastmodifiedDate(new Date().toISOString());
        if (pageState.toLowerCase() !== PUBLISHED.toLowerCase()) {
          setIsLoading(false);
          if (resp?.data?.authoring_createContent?.isExist === true) {
            setOpenPageExistModal(true);
            setPageStatus(pageState);
            setWorkflowStatus(isWorkflow);
          } else {
            if (!isWorkflow) {
              ShowToastSuccess(`${t("poll")} ${t("saved_toast")}`);
            }
            setIsDraft(false);
            const { createdBy } = pollInstance.CommonFields;
            const { title, description } = updateTempObj.current;
            const workflowObj = {
              createdBy,
              title,
              description,
              path: resp?.data?.authoring_createContent?.path,
              workflow_status: workflowKeys.draft,
              tag_name: capitalizeFirstLetter(ctype),
              last_modifiedBy: createdBy,
            };
            setWorkflow({ ...workflow, ...workflowObj });
            if (isWorkflow) {
              workflowSubmitRequest(workflowObj, workflowKeys.approve);
            }
          }
        } else {
          if (resp?.data?.authoring_createContent?.isExist === true) {
            setOpenPageExistModal(true);
            setPageStatus(pageState);
          } else {
            publishPoll(
              resp?.data?.authoring_createContent?.path.substring(
                resp?.data?.authoring_createContent?.path.lastIndexOf("/") + 1,
              ),
            );
          }
        }
        const pageUrl = resp?.data?.authoring_createContent?.path.substring(
          resp?.data?.authoring_createContent?.path.lastIndexOf("/") + 1,
        );
        pollRef.current.page = pageUrl;
        setDraftPageURL(pageUrl);
      })
      .catch((error: any) => {
        const statusCode = error?.networkError?.statusCode || 0;

        setTimerState(false);
        setLastmodifiedDate("");
        if (error?.graphQLErrors[0]) {
          ShowToastError(error.graphQLErrors[0].message, statusCode);
        } else {
          ShowToastError(t("api_error_toast"), statusCode);
        }
      });
  };
  const pageExistYesButtonHandle = () => {
    setOpenPageExistModal(false);
    if (pageStatus.toLowerCase() === DRAFT.toLowerCase()) {
      createPoll(DRAFT, true, workflowStatus);
    } else if (pageStatus.toLowerCase() === PUBLISHED.toLowerCase()) {
      createPoll(PUBLISHED, true);
    }
  };
  const pageExistCloseHandle = () => {
    unsavedChanges.current = true;
    setOpenPageExistModal(false);
    setIsLoading(false);
  };
  useEffect(() => {
    if (timerState) {
      localStorage.setItem("contentTypeTimerState", "true");
    }
  }, [timerState]);
  // Slug is already validated by guardSlugThenProceed before this is ever
  // called (from savePoll/publish, ahead of the createPoll/updatePOLL branch).
  const updatePOLL = (status, isWorkflow = true, props = {}, event_step = "") => {
    setIsLoading(true);
    const newTempData = JSON.parse(JSON.stringify(pollInstance));
    const tempObjField = {
      background_content: buildBackgroundContent(),
      question_background_content: {
        objectType: pollState?.queBackgroundImg
          ? "image"
          : pollState?.queBackgroundColor
            ? "color"
            : "",
        Url: pollState?.queBackgroundImg,
        Title: "",
        Thumbnail: pollState?.queBackgroundImg,
        Color: pollState?.queBackgroundColor,
        original_image: pollState?.question_original_image,
        published_images: pollState?.question_published_images,
      },
      display_scores: pollState?.scoreBy,
      poll_description: pollState?.poll_description,
      poll_title: pollState?.poll_title,
      poll_question: pollState?.poll_title,
      is_image_option: addImage,
      options_compound_fields: answers.map((ans) => {
        return {
          option_image: {
            url: addImage ? ans.image : "",
            ext: ans.ext || "",
            title: "Option Image",
          },
          is_correct: ans.status,
          option_text: ans.option,
          option_id: ans.id,
        };
      }),
      original_image: pollState?.original_image,
      published_images: pollState?.published_images,
    };
    const updatePollToSend = {
      // ...newTempData,
      CommonFields: {
        ...newTempData.CommonFields,
        ...updateTempObj.current,
        structure_data: pollState?.structure_data,
        current_page_url: `/${
          currentPollData.current !== "" ? currentPollData.current : draftPageURL
        }`,
        page: draftPageURL ? draftPageURL : currentPollData.current,
        page_state: status,
        creationDate: new Date().toISOString(),
        modificationDate: new Date().toISOString(),
        createdBy: username,
        page_lastmodifiedby: username,
        parent_page_url: "/",
        seo_enable: pollState?.seo_enable,
        analytics_enable: pollState?.analytics_enable,
        title: pollState?.title,
        description: pollState?.description,
        // short_title: pollState?.short_title,
        // short_description: pollState?.short_description,
        is_featured: isFeatured,
        tag_sm: tagCategory,
        slug: pollState?.slug,
      },
      ObjectFields: {
        ...newTempData.ObjectFields,
        ...tempObjField,
      },
    };
    updatepollmutate({
      variables: {
        contenttype: ctype,
        input: appendBrandNameIfPresent(updatePollToSend, isShared),
      },
      context: {
        skipBrandNameHeader: isShared,
      },
    })
      .then(() => {
        setTimerState(true);
        setLastmodifiedDate(new Date().toISOString());
        if (status && status?.toLowerCase() === DRAFT.toLowerCase()) {
          setIsLoading(false);
          if (!isWorkflow) {
            ShowToastSuccess(`${t("poll")} ${t("updated_toast")}`);
          } else {
            workflowSubmitRequest(props, event_step);
          }
          unsavedChanges.current = false;
          setShowExitWarning(false);
          setIsEdited(false);
        } else {
          publishPoll(draftPageURL ? draftPageURL : currentPollData.current);
        }
      })
      .catch((error: any) => {
        const statusCode = error?.networkError?.statusCode || 0;

        setTimerState(false);
        setLastmodifiedDate("");
        ShowToastError(t("api_error_toast"), statusCode);
        setIsLoading(false);
      });
  };
  const handleCloseDialog = () => {
    setShowPublishConfirm(false);
    setShowWorkflowSubmit(false);
  };
  const savePoll = async (status = true, props = {}, event_step = "") => {
    setShowExitWarning(false);
    setPollState({
      ...pollState,
      tags: tagArr,
    });

    // if (fieldValidation(pollState, status, tagArr, answers, addImage, isError, setIsError)) {
    const pageURL = currentPollData.current
      ? currentPollData.current
      : pollRef?.current?.title.replace(/[^A-Z0-9]+/gi, "-").toLowerCase();
    updateCurrentInstance(pageURL);

    if (showExitWarning) {
      setShowExitWarning(false);
      setIsEdited(false);
    }
    if (!(await guardSlugThenProceed())) return;
    if (!currentPollData.current && isDraft) {
      createPoll(DRAFT, false, status);
    } else {
      updatePOLL(DRAFT, status, props, event_step);
    }
    //}
  };

  const publish = async () => {
    setPollState({
      ...pollState,
      tags: tagArr,
    });
    if (fieldValidation(pollState, true, tagArr, answers, addImage, isError, setIsError)) {
      const pageURL = currentPollData.current
        ? currentPollData.current
        : pollRef?.current?.title.replace(/[^A-Z0-9]+/gi, "-").toLowerCase();
      updateCurrentInstance(pageURL);

      if (showExitWarning) {
        setShowExitWarning(false);
        setIsEdited(false);
      }

      if (!(await guardSlugThenProceed())) return;
      if (!currentPollData.current && isDraft) {
        createPoll(PUBLISHED, false, false);
      } else {
        updatePOLL(PUBLISHED, false);
      }
    }
  };

  const handleSelectedImage = (image, keyName, id?: any) => {
    try {
      if (key === "imagevideoURL") {
        setPollState({
          ...pollState,
          [key]: image?.Thumbnail,
          thumbnailURL: image?.Thumbnail,
          // socialShareImgURL: relativeUrl,
        });
        pollRef.current = {
          ...pollRef.current,
          [key]: image?.Thumbnail,
          // socialShareImgURL: relativeUrl,
        };

        unsavedChanges.current = true;
        setIsEdited(true);
      } else if (key === "answers") {
        const gcbPath = image?.original_image_relative_path;
        const gcbExt = image?.ext;
        setAnswers(
          answers.map((answer) =>
            answer.id === answerId
              ? { ...answer, image: gcbPath || "", ext: gcbExt || "" }
              : answer,
          ) as [],
        );
        setIsError({ ...isError, emptyImageOptions: false });
      } else {
        setPollState({ ...pollState, [keyName]: image?.Thumbnail });
        pollRef.current = {
          ...pollRef.current,
          [keyName]: image?.Thumbnail,
          // socialShareImgURL: relativeUrl,
        };
        unsavedChanges.current = true;
      }
      toggleGallery(false);
    } catch (error) {
      if (keyName === "imagevideoURL") {
        setPollState({
          ...pollState,
          [keyName]: image?.Thumbnail,
          thumbnailURL: image?.Thumbnail,
          socialShareImgURL: "",
        });
        pollRef.current = {
          ...pollRef.current,
          [keyName]: image?.Thumbnail,
          socialShareImgURL: "",
        };

        unsavedChanges.current = true;
        setIsEdited(true);
      } else if (keyName === "answers") {
        const gcbPath = image?.original_image_relative_path;
        const gcbExt = image?.ext;
        const answerImgUrl = gcbPath ? `${gcbPath}.${gcbExt}` : "";
        setAnswers(
          answers.map((answer) =>
            answer.id === id ? { ...answer, image: answerImgUrl } : answer,
          ) as [],
        );
        setIsError({ ...isError, emptyImageOptions: false });
      } else {
        setPollState({ ...pollState, [keyName]: image?.Thumbnail });
        pollRef.current = {
          ...pollRef.current,
          [keyName]: image?.Thumbnail,
          socialShareImgURL: "",
        };
        unsavedChanges.current = true;
      }
      keyName === "socialShareImgURL" && ShowToastError(t("api_error_toast"));
      toggleGallery(false);
    }
  };
  const toggleGallery = (toggleState) => {
    setGalleryState(toggleState);
  };
  const showGallery = (gType, keyName, id?: any) => {
    window.scrollTo(0, 0);
    galleryType.current = gType;
    setGalleryState(true);
    setKey(keyName);
    if (id) {
      setAnswerId(id);
    }
  };
  const returnBack = () => {
    if (unsavedChanges.current === true) {
      setShowExitWarning(true);
    } else {
      navigate("/content/poll");
    }
  };
  const handleTagOnChange = (event, category = "") => {
    let tagsArray = [...tagArr];
    let tagsCategory: any = [...tagCategory];
    if (event.target.checked && tagsArray?.length > 14) {
      event.target.checked = false;
      ShowToastError(t("allowed_tags_toast"));
    } else {
      if (event.target.checked) {
        tagsArray = [...tagArr, event.target.value];
        tagsCategory = [...tagsCategory, `${category}/${event.target.value}`];
      } else {
        tagsArray.splice(tagArr.indexOf(event.target.value), 1);
        tagsCategory.splice(tagsCategory.indexOf(`${category}/${event.target.value}`), 1);
      }
      setTagArr(tagsArray);
      setTagCategory(tagsCategory);
      setPollState({
        ...pollState,
        tagsSocialShare: tagsArray,
      });
      pollRef.current = {
        ...pollRef.current,
        tags: tagsArray,
        tagsSocialShare: tagsArray, //[...pollState.tagsSocialShare],
      };
      setIsEdited(true);
      unsavedChanges.current = true;
    }
  };

  const handleSocialShareChange = (event) => {
    const { name } = event.target;
    pollRef.current = {
      ...pollRef.current,
      [name]: event.target.value,
    };
    setPollState({
      ...pollState,
      [name]: event.target.value,
    });

    unsavedChanges.current = true;
  };

  const handleSocialShareTagChange = (_, value) => {
    const result = value.filter((str) => str.trim()?.length !== 0);
    const updatedtags = result.filter((c, index) => {
      return result.indexOf(c) === index;
    });
    pollRef.current = {
      ...pollRef.current,
      tagsSocialShare: updatedtags,
    };
    setPollState({
      ...pollState,
      tagsSocialShare: updatedtags,
    });
  };

  const handleSocialShareImageChange = (data) => {
    setPollState((prevState) => {
      return {
        ...prevState,
        socialShareImgURL: data?.original_image?.Thumbnail || "",
      };
    });
    pollRef.current = {
      ...pollRef.current,
      socialShareImgURL: data?.original_image?.Thumbnail || "",
    };
    unsavedChanges.current = true;
  };

  const handleAnalyticsSeoChange = (event, keyName) => {
    pollRef.current = { ...pollRef.current, [keyName]: event?.target?.checked };
    setPollState((prevState) => {
      return {
        ...prevState,
        [keyName]: event?.target?.checked,
      };
    });
  };

  const handleStrucutreDataChange = (structureData) => {
    setPollState((prevState) => {
      return {
        ...prevState,
        structure_data: JSON.stringify(structureData),
      };
    });
  };

  const fetchPolls = async () => {
    await runFetchContentByPath({
      variables: { contentType: ctype, path: currentPollData.current },
    })
      .then((res) => {
        if (res?.data?.authoring_getCmsContentByPath) {
          setIsLoading(false);
          const contentObj = res?.data?.authoring_getCmsContentByPath;
          const {
            title,
            // short_title: shortTitle,
            // short_description: shortDesc,
            description,
            background_content: backgroundContent,
            question_background_content: questionBackgroundContent,

            display_scores: scoreBy,
            tags,
            settingsProperties,
            poll_title: pollTitle,
            poll_description: pollDesc,
            is_image_option: isImgOpt,
            options_compound_fields: options,
            path,
            workflow_status,
            stages,
            tag_name,
            last_modifiedBy,
            createdBy,
            task_status,
            user_id,
            user_name,
            is_featured,
          } = contentObj;
          setIsFeatured(is_featured);
          setWorkflow({
            path,
            workflow_status,
            stages,
            tag_name,
            last_modifiedBy,
            createdBy,
            role,
            title,
            enable: stages?.length > 0 ? true : false,
            login_user_id,
            task_status,
            task_user_id: user_id,
            task_user_name: user_name,
            description,
          });
          const temp = options.map((x) => {
            return {
              id: x.option_id,
              option: x.option_text,
              image: x.option_image.url,
              ext: x.option_image.ext || "",
            };
          });
          setAddImage(isImgOpt);
          setAnswers([...temp]);
          const tempObj = {
            ...pollState,
            title: title,
            // short_title: shortTitle,
            // short_description: shortDesc,
            description: description,
            imagevideoURL: backgroundContent?.Url,
            colorCode: backgroundContent?.Color,
            poll_title: pollTitle,
            poll_description: pollDesc,
            queBackgroundColor: questionBackgroundContent?.Color,
            queBackgroundImg: questionBackgroundContent?.Url,
            scoreBy: scoreBy,
            tags: tags,
            is_schedule_publish: settingsProperties?.is_schedule_publish,
            is_schedule_unpublish: settingsProperties?.is_schedule_unpublish,
            schedule_publish_datetime: settingsProperties?.schedule_publish_datetime,
            schedule_unpublish_datetime: settingsProperties?.schedule_unpublish_datetime,
            socialShareImgURL: settingsProperties?.socialog_image,
            titleSocialShare: settingsProperties?.socialog_title,
            descriptionSocialShare: settingsProperties?.socialog_description,
            tagsSocialShare: settingsProperties?.keywords,
            seo_enable: res?.data?.authoring_getCmsContentByPath?.seo_enable,
            structure_data: res?.data?.authoring_getCmsContentByPath?.structure_data,
            analytics_enable: res?.data?.authoring_getCmsContentByPath?.analytics_enable,
            original_image: contentObj.original_image,
            published_images: contentObj.published_images,
            question_original_image: contentObj.question_background_content.original_image,
            question_published_images: contentObj.question_background_content.published_images,
            // Add FCM notification fields
            notification: res?.data?.authoring_getCmsContentByPath?.notification || false,
            notification_title:
              res?.data?.authoring_getCmsContentByPath?.notification_title ||
              res?.data?.authoring_getCmsContentByPath?.title,
            notification_desc:
              res?.data?.authoring_getCmsContentByPath?.notification_desc ||
              res?.data?.authoring_getCmsContentByPath?.description,
            notification_image: res?.data?.authoring_getCmsContentByPath?.notification_image || "",
            notification_channel:
              res?.data?.authoring_getCmsContentByPath?.notification_channel || [],
            notification_topics:
              res?.data?.authoring_getCmsContentByPath?.notification_topics || [],
            content_behind_rendering:
              res?.data?.authoring_getCmsContentByPath?.content_behind_rendering || "",
            slug: res?.data?.authoring_getCmsContentByPath?.slug,
          };
          originalSlugRef.current = res?.data?.authoring_getCmsContentByPath?.slug || "";
          setPollState(tempObj);
          setPollInstance({
            ...tempObj,
            options_compound_fields:
              res?.data?.authoring_getCmsContentByPath?.options_compound_fields,
            page_state: res?.data?.authoring_getCmsContentByPath?.page_state,
          });
          pollRef.current = {
            title: res?.data?.authoring_getCmsContentByPath?.title,
            // short_title: res?.data?.authoring_getCmsContentByPath?.short_title,
            // short_description: res?.data?.authoring_getCmsContentByPath?.short_description,
            description: res?.data?.authoring_getCmsContentByPath?.description,
            imagevideoURL: res?.data?.authoring_getCmsContentByPath?.background_content?.Url,
            tags: res?.data?.authoring_getCmsContentByPath?.tags,
            titleSocialShare:
              res?.data?.authoring_getCmsContentByPath?.settingsProperties?.socialog_title,
            descriptionSocialShare:
              res?.data?.authoring_getCmsContentByPath?.settingsProperties?.socialog_description,
            socialShareImgURL:
              res?.data?.authoring_getCmsContentByPath?.settingsProperties?.socialog_image,
            tagsSocialShare: res?.data?.authoring_getCmsContentByPath?.settingsProperties?.keywords,
            // Add FCM notification fields
            notification: res?.data?.authoring_getCmsContentByPath?.notification || false,
            notification_title:
              res?.data?.authoring_getCmsContentByPath?.notification_title ||
              res?.data?.authoring_getCmsContentByPath?.title,
            notification_desc:
              res?.data?.authoring_getCmsContentByPath?.notification_desc ||
              res?.data?.authoring_getCmsContentByPath?.description,
            notification_image: res?.data?.authoring_getCmsContentByPath?.notification_image || "",
            notification_channel:
              res?.data?.authoring_getCmsContentByPath?.notification_channel || [],
            notification_topics:
              res?.data?.authoring_getCmsContentByPath?.notification_topics || [],
            content_behind_rendering:
              res?.data?.authoring_getCmsContentByPath?.content_behind_rendering,
          };
          setTagArr(res?.data?.authoring_getCmsContentByPath?.tags);
          if (res.data.authoring_getCmsContentByPath.tag_sm?.length > 0) {
            setTagCategory(res.data.authoring_getCmsContentByPath.tag_sm); //initializing tags with category
          }
          taglength.current = res?.data?.authoring_getCmsContentByPath?.tags?.length;
        }
      })
      .catch(() => {
        ShowToastError(t("api_error_toast"));
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (Object.keys(currentContent).length > 0) {
      setPollState(currentContent);
      // Restoring from a Preview round-trip (not a fresh fetch) — treat the
      // restored slug as the new baseline so re-saving it unchanged doesn't
      // trigger a stale/empty excludeValue and a false "not unique" result.
      originalSlugRef.current = currentContent?.slug || "";
      pollRef.current = currentContent;
      setAnswers(
        currentContent?.options_compound_fields?.map((x) => {
          return {
            id: x.option_id,
            option: x.option_text,
            image: x.option_image.url,
          };
        }),
      );
      setAddImage(currentContent?.addImage);
      setTagArr(currentContent?.tags);
      setWorkflow(currentContent.workflow);
      setIsDraft(currentContent?.isDraft);
      setDraftPageURL(currentContent?.draftPageURL);
    } else if (currentPollData.current && unsavedChanges.current !== true) {
      setIsLoading(true);
      fetchPolls();
    }
  }, [currentContent]);

  useEffect(() => {
    if (Object.keys(tagData).length === 0) {
      runFetchTagList({
        variables: { start: 0, rows: 1000 },
      })
        .then((res) => {
          if (res?.data?.authoring_getTagsList) {
            setTagData(res?.data?.authoring_getTagsList);
            setScrollToView(
              pollPageUrl.searchParams.get("open")
                ? (pollPageUrl.searchParams.get("open") as string)
                : "",
            );
          }
        })
        .catch(() => {
          ShowToastError(t("api_error_toast"));
        });
    }
  }, []);

  const isInViewport = (element) => {
    const mainElement = document.querySelector(`#${element}`);
    if (mainElement) {
      const rect = mainElement.getBoundingClientRect();
      return (
        rect.top <= window.pageYOffset + window.innerHeight &&
        rect.left <= window.pageXOffset + window.innerWidth &&
        rect.top >= window.pageYOffset &&
        rect.left >= window.pageXOffset
      );
    }
    return false;
  };
  const scrollHandler = () => {
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
    }
    const timeOutId = setTimeout(() => {
      const container = document.getElementById("scrollableDiv");
      const active = icons.find((i) => isInViewport(i.id));
      if (active && active.tooltip !== parentToolTip) {
        setParentToolTip(active.tooltip);
      }
      if (
        container &&
        Math.abs(container?.scrollHeight - container?.clientHeight - container?.scrollTop) < 1
      ) {
        setParentToolTip("seo");
      }
    }, 100);
    scrollDebounceRef.current = timeOutId;
  };

  useEffect(() => {
    const dataHolder = document.getElementById("scrollableDiv");
    dataHolder?.addEventListener("scroll", scrollHandler);
    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, []);

  const closeButtonHandle = () => {
    if (qusUnsavedChanges.current === true) {
      setOpenAddQuestion(false);
      setShowExitWarning(false);
      qusUnsavedChanges.current = false;
    } else {
      navigate("/content/poll");
    }
  };
  const handelPreview = () => {
    const backgroundContent = {
      objectType: pollState?.imagevideoURL ? "image" : pollState?.colorCode ? "color" : "",
      Url: pollState?.thumbnailURL,
      Color: pollState?.colorCode,
    };
    const questionBackgroundContent = {
      objectType: pollState?.queBackgroundImg
        ? "image"
        : pollState?.queBackgroundColor
          ? "color"
          : "",
      Url: pollState?.queBackgroundImg,
      Title: "",
      Thumbnail: pollState?.queBackgroundImg,
      Color: pollState?.queBackgroundColor,
    };
    const optionsCompoundFields = answers.map((ans) => {
      return {
        option_image: {
          url: addImage ? ans.image : "",
          title: "Option Image",
        },
        is_correct: ans.status,
        option_text: ans.option,
        option_id: ans.id,
      };
    });
    const tempObj = {
      ...pollState,
      background_content: backgroundContent,
      options_compound_fields: optionsCompoundFields,
      question_background_content: questionBackgroundContent,
      contentType: ctype,
      workflow: workflow,
      isDraft: isDraft,
      draftPageURL: draftPageURL,
      addImage: addImage,
    };
    dispatch(previewContent(tempObj));
    navigate("/content/preview", {
      state: { contentType: MAPPING.Poll, pageName: currentPollData?.current || draftPageURL },
    });
  };

  //create comment
  const createComment = () => {
    const createCommentRequest = {
      document_path: `/content/documents/hclplatformx/${getCurrentLang()}/poll/${currentPollData.current}`,
      status: false,
      document_type: "Poll",
      created_by: username,
      last_modified_by: username,
      reviewer_comments: [comments],
    };

    return commentsApi.createOrUpdateComment({
      input: createCommentRequest,
    });
  };

  useEffect(() => {
    if (
      Object.keys(currentContent).length === 0 &&
      !currentPollData.current &&
      tagData?.length > 0
    ) {
      handleTagOnChange({
        target: {
          checked: true,
          value: "Polls",
        },
      });
    }
  }, [tagData?.length > 0]);

  useEffect(() => {
    const structureData = updateStructureData();
    setPollState((prevState) => {
      return {
        ...prevState,
        structure_data: JSON.stringify(structureData),
      };
    });
  }, [pollState?.poll_description, pollState?.poll_title, answers]);

  useEffect(() => {
    setTagCategory(getCategoryTags(tagData, tagArr));
  }, [tagData, tagArr]);
  return (
    <>
      <Box
        sx={{
          backgroundColor: "#FFF",
        }}>
        {galleryState && (
          <DamContentGallery
            handleImageSelected={handleSelectedImage}
            toggleGallery={toggleGallery}
            assetType={"Image"}
            dialogOpen={galleryState}
            isCrop={false}
          />
        )}
      </Box>

      <Box
        sx={{
          display: isClickedQueList || openAddQuestion ? "none" : "initial",
        }}>
        {isLoading && <Loader />}
        <Box>
          <Box>
            <CreateHeader
              hasPreviewButton={previewButton}
              handelPreview={handelPreview}
              createText={currentPollData.current ? t("edit_poll") : t("create_poll")}
              handleReturn={returnBack}
              isQuiz
              hasPublishButton={publishButton}
              hasSaveButton={saveButton}
              handleSaveOrPublish={savePoll}
              publishText={t("publish")}
              saveText={t("save_as_draft")}
              previewText={t("preview")}
              toolTipText={previewTooltip ? t("preview_tooltip") : t("preview_tooltip1")}
              saveVariant='secondaryButton'
              handlePublish={publish}
              category={CATEGORY_CONTENT}
              subCategory={ContentType.Poll}
              workflow={workflow}
              hasTimerState={timerState}
              lastModifiedDate={lastmodifiedDate}
              setEnableWorkflowHistory={setEnableWorkflowHistory}
              createComment={createComment}
              setIsFeatured={setIsFeatured}
              isFeatured={isFeatured}
              publishDisabled={isLoading}
            />
            <Divider></Divider>
          </Box>

          <Box
            sx={{
              position: "relative",
              height: {
                sm: "calc(100vh - 125px)",
                xs: "calc(100vh - 45px)",
              },
              overflowY: "scroll",
              overflowX: "hidden",
            }}
            id='scrollableDiv'>
            {!isClickedQueList && !galleryState && !enableWorkflowHistory && (
              <Box
                sx={{
                  position: "fixed",
                  top: "25%",
                  right: { sm: "5px", xs: 0 },
                  zIndex: 1000,
                }}>
                <ContentPageScroll
                  icons={icons}
                  parentToolTip={parentToolTip}
                  scrollToView={scrollToView}
                />
              </Box>
            )}
            {enableWorkflowHistory ? (
              <WorkflowHistory
                workflow={workflow}
                setEnableWorkflowHistory={setEnableWorkflowHistory}
              />
            ) : (
              <>
                <TitleDescription
                  state={pollState}
                  setState={setPollState}
                  unsavedChanges={unsavedChanges}
                  pollRef={pollRef}
                  isDraft={isDraft}
                  setFieldChanges={setFieldChanges}
                  isError={isError}
                  setIsError={setIsError}
                />
                <ImageVideo
                  state={pollState}
                  setState={setPollState}
                  pollRef={pollRef}
                  unsavedChanges={unsavedChanges}
                  isError={isError}
                  setIsError={setIsError}
                />
                <AddQuestion
                  // saveQuestionCallBack={saveQuestionCallBack}
                  qusUnsavedChanges={unsavedChanges}
                  showGallery={showGallery}
                  state={pollState}
                  setState={setPollState}
                  answers={answers}
                  setAnswers={setAnswers}
                  addImage={addImage}
                  setAddImage={setAddImage}
                  setFieldChanges={setFieldChanges}
                  isError={isError}
                  setIsError={setIsError}
                  // selectedImage={selectedImage}
                />
                <Result state={pollState} setState={setPollState} />
                <ChooseTags
                  tagData={tagData}
                  selectedTag={tagArr}
                  subCategory={ContentTypeTagName.polls}
                  handleTagOnChange={handleTagOnChange}
                  isEdit={Boolean(currentPollData.current)}
                />
                <SocialShare
                  titleSubTitles={socialShareTitleSubTitles()}
                  state={pollState}
                  handleOnBlur={handleSocialShareChange}
                  handleChange={() => {}}
                  handleTagChange={handleSocialShareTagChange}
                  handleImageChange={handleSocialShareImageChange}
                />
                <Analytics
                  titleSubTitles={analyticsTitleSubTitles()}
                  state={pollState}
                  handleChange={handleAnalyticsSeoChange}
                />
                <Seo
                  titleSubTitles={seoTitleSubTitles()}
                  state={pollState}
                  handleChange={handleAnalyticsSeoChange}
                  handleStrucutreDataChange={handleStrucutreDataChange}
                  slugField={slugField}
                />
                {hasNotificationAccess ? (
                  <FCMNotification
                    titleSubTitles={notificationTitleSubTitles()}
                    sectionNumber='10'
                    state={pollState}
                    setState={setPollState}
                    unsavedChanges={unsavedChanges}
                    contentRef={pollRef}
                    handleOnBlur={handleSocialShareChange}
                    handleChange={handleAnalyticsSeoChange}
                  />
                ) : null}
                <ContentStatus
                  titleSubTitles={contentStatusTitleSubTitles()}
                  state={pollState}
                  setState={setPollState}
                  unsavedChanges={unsavedChanges}
                  contentRef={pollRef}
                  sectionNumber={hasNotificationAccess ? "11" : "10"}
                />
              </>
            )}
          </Box>
        </Box>
        <CommonPlateformXDialog
          isDialogOpen={showExitWarning}
          title={t("save_warn_title")}
          subTitle={t("save_warn_subtitle")}
          closeButtonText={t("stay_here")}
          confirmButtonText={t("take_me_out")}
          closeButtonHandle={() => {
            setShowExitWarning(false);
          }}
          confirmButtonHandle={closeButtonHandle}
          modalType='unsavedChanges'
        />
        {/* <PlateformXDialog
          isDialogOpen={onSavedModal}
          title={t("save_as_draft")}
          subTitle={t("poll_draft_subtitle")}
          closeButtonText={t("edit")}
          confirmButtonText={t("go_to_listing")}
          closeButtonHandle={crossButtonHandle}
          confirmButtonHandle={() => navigate("/content/poll")}
          crossButtonHandle={crossButtonHandle}
          modalType='draft'
          closeIcon={<CreateRoundedIcon />}
        /> */}
        {showPublishConfirm || showWorkflowSubmit ? (
          <CommonPlateformXDialog
            isDialogOpen={showPublishConfirm || showWorkflowSubmit}
            title={t("congratulations")}
            subTitle={
              showPublishConfirm
                ? `${t("your")} ${t("poll")} ${t("publish_popup_message")}`
                : t("requested_action")
            }
            closeButtonHandle={handleCloseDialog}
            confirmButtonText={t("go_to_listing")}
            confirmButtonHandle={() => navigate("/content/poll")}
            modalType='publish'
          />
        ) : null}
        {openPageExistModal ? (
          <CommonPlateformXDialog
            isDialogOpen={openPageExistModal}
            title={`${t("poll")} ${t("already_exists")}`}
            subTitle={t("conformation")}
            closeButtonText={t("no")}
            confirmButtonText={t("yes")}
            closeButtonHandle={pageExistCloseHandle}
            confirmButtonHandle={pageExistYesButtonHandle}
            modalType=''
          />
        ) : null}
      </Box>
      <CommentListPanel></CommentListPanel>
    </>
  );
};
